import React, { useEffect, useState } from "react";
import { useMobileFormShell } from "@/lib/useMobileFormShell";
import { Link, useNavigate } from "react-router-dom";
import {
  ClipboardList, HardHat, Truck, AlertTriangle, FileSignature,
  QrCode, Camera, ChevronRight, LogOut, ArrowLeft, Sun,
  WifiOff, Activity,
} from "lucide-react";
import { MasciLogo } from "@/components/MasciLogo";
import DemoSubmissionBanner from "@/components/DemoSubmissionBanner";
import { clearAdminToken } from "@/lib/adminAuth";
import { clearPmToken } from "@/lib/pmAuth";
import { clearShopToken } from "@/lib/shopAuth";

/**
 * FieldQuickActions — the Foreman / Operator quick-action hub.
 *
 * NOT a dashboard. The opposite of a dashboard. This is the surface a
 * field user sees when they sit in the truck cab at 5:45 AM. Five
 * massive tap-tiles (96px+), each opening a real workflow — daily
 * report, toolbox talk, pre-op walk-around, incident report, JHP ack.
 *
 * Design rules:
 *   · Touch-target minimum 96px (h-24)
 *   · One tap to open · zero typing on this surface
 *   · High contrast for sunlight reading
 *   · Mobile-first; on desktop, the tiles spread but never become small
 *   · Identity strip at top reads from forgeops.demo.identity
 *   · DemoSubmissionBanner shown so persistence loop closes here too
 *
 * Tone: brutally simple. No marketing copy. No cinema. The kind of
 * surface a foreman opens on his phone three times a day without thinking.
 */

/**
 * Action sets per role. Operator workflows are intentionally NARROWER:
 * operators do NOT submit daily reports (that's foreman/superintendent
 * territory) and do NOT lead toolbox talks (they attend & sign in).
 * The cab-friendly operator surface focuses on equipment + safety.
 */
const ACTIONS_FOREMAN = [
  {
    key: "daily",
    icon: ClipboardList,
    label: "Daily Report",
    sub: "≈ 45 sec from the cab",
    href: "/daily/new",
    accent: "#FF6A00",
  },
  {
    key: "toolbox",
    icon: HardHat,
    label: "Toolbox Talk",
    sub: "QR scan · 1-tap sign-off",
    href: "/meetings/new",
    accent: "#FFD000",
  },
  {
    key: "preop",
    icon: Truck,
    label: "Pre-Op Walk-Around",
    sub: "≈ 30 sec · photo on FAIL",
    href: "/inspections/new",
    accent: "#FF6A6A",
  },
  {
    key: "incident",
    icon: AlertTriangle,
    label: "Report an Incident",
    sub: "Near-miss · injury · damage",
    href: "/incidents/new",
    accent: "#FF6A6A",
  },
  {
    key: "jhp",
    icon: FileSignature,
    label: "Acknowledge JHP",
    sub: "Scan poster · 1 tap",
    href: "/jha-acknowledgement",
    accent: "#65DA76",
  },
];

const ACTIONS_OPERATOR = [
  {
    key: "preop",
    icon: Truck,
    label: "Pre-Op Walk-Around",
    sub: "≈ 30 sec · photo on FAIL",
    href: "/inspections/new",
    accent: "#FF6A00",
  },
  {
    key: "defect",
    icon: AlertTriangle,
    label: "Report Equipment Defect",
    sub: "Photo · brief note · routes to Shop",
    href: "/inspections/new",
    accent: "#FF6A6A",
  },
  {
    key: "incident",
    icon: AlertTriangle,
    label: "Report an Incident",
    sub: "Near-miss · injury · damage",
    href: "/incidents/new",
    accent: "#FF6A6A",
  },
  {
    key: "jhp",
    icon: FileSignature,
    label: "Acknowledge JHP",
    sub: "Scan poster · 1 tap",
    href: "/jha-acknowledgement",
    accent: "#65DA76",
  },
  {
    key: "toolbox-attend",
    icon: HardHat,
    label: "Sign in to Toolbox Talk",
    sub: "QR sign-off · attendance only",
    href: "/meetings/new",
    accent: "#FFD000",
  },
];

const ROLE_TITLE = {
  "foreman@forgeops.demo":  { chip: "FOREMAN · FIELD EXECUTION", title: "Run the day, foreman.",        sub: "Pick a workflow. The system handles the rest." },
  "operator@forgeops.demo": { chip: "OPERATOR · UNIT READINESS", title: "Walk-around first, operator.", sub: "Open Pre-Op. Photo on FAIL. Move."             },
  fallback: { chip: "FIELD WORKER", title: "Pick a workflow.", sub: "One tap. The system files it." },
};

export default function FieldQuickActions() {
  useMobileFormShell();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("forgeops.demo.identity");
      if (raw) setIdentity(JSON.parse(raw));
    } catch { /* */ }
  }, []);

  const cfg = ROLE_TITLE[identity?.email] || ROLE_TITLE.fallback;
  const name = identity?.name || "Field Worker";
  const isOperator = identity?.email === "operator@forgeops.demo";
  const actions = isOperator ? ACTIONS_OPERATOR : ACTIONS_FOREMAN;
  const todaysJobValue = isOperator ? "EX-210 · Pre-Op due" : "FO-1003 · Municipal";

  const signOut = () => {
    try {
      clearAdminToken(); clearPmToken(); clearShopToken();
      window.localStorage.removeItem("forgeops.demo.identity");
    } catch { /* */ }
    navigate("/demo");
  };

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A" }} data-testid="field-quick-actions">
      {/* Header — minimal, single-line, sunlight-readable */}
      <header
        className="border-b sticky top-0 z-30"
        style={{ background: "#070707", borderColor: "#1A1A1A" }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/demo" className="inline-flex items-center gap-2" data-testid="fqa-home-link">
            <MasciLogo className="h-7 sm:h-8 w-auto" />
          </Link>
          <button
            type="button"
            onClick={signOut}
            data-testid="fqa-signout"
            className="inline-flex items-center gap-1.5 px-3 h-10 rounded font-mono text-[10px] uppercase tracking-[0.25em]"
            style={{ background: "transparent", color: "#BDBDBD", border: "1px solid #2A2A2A" }}
            aria-label="Sign out"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <DemoSubmissionBanner portal="admin" />

        {/* Identity strip — tight */}
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>
            {cfg.chip}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight mt-1">
            {cfg.title.replace(/foreman|operator|field worker/i, name)}
          </h1>
          <p className="text-sm mt-1" style={{ color: "#BDBDBD" }}>
            {cfg.sub}
          </p>
        </div>

        {/* Field-state strip — the kind of detail crews actually look for */}
        <div
          className="grid grid-cols-3 gap-px mb-6 rounded-md overflow-hidden"
          style={{ background: "#1A1A1A" }}
          data-testid="fqa-field-state"
        >
          <FieldStat icon={Sun}      label="Conditions"   value="Clear · 62°F" />
          <FieldStat icon={WifiOff}  label="Mode"         value="Online · syncing" tone="#65DA76" />
          <FieldStat icon={Activity} label="Today's job"  value={todaysJobValue} />
        </div>

        {/* Quick actions — massive tap targets */}
        <div className="space-y-2.5" data-testid="fqa-actions" data-role={identity?.email || ""}>
          {actions.map((a) => (
            <ActionTile key={a.key} action={a} />
          ))}
        </div>

        {/* Tiny QR helper — restraint */}
        <div className="mt-6 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
          <QrCode className="w-3 h-3" />
          Most workflows can also be opened by scanning a poster QR
        </div>
      </main>
    </div>
  );
}

function FieldStat({ icon: Icon, label, value, tone = "#FFFFFF" }) {
  return (
    <div className="px-3 py-2.5 flex items-center gap-2" style={{ background: "#0F0F0F" }}>
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded shrink-0"
        style={{ background: "rgba(255,106,0,0.10)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}
      >
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="min-w-0">
        <div className="font-mono text-[8px] uppercase tracking-[0.2em] truncate" style={{ color: "#BDBDBD" }}>
          {label}
        </div>
        <div className="text-[12px] font-bold leading-tight truncate" style={{ color: tone }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function ActionTile({ action }) {
  const Icon = action.icon;
  return (
    <Link
      to={action.href}
      data-testid={`fqa-${action.key}`}
      className="group block w-full rounded-xl transition active:scale-[0.99]"
      style={{
        background: "#0F0F0F",
        border: "1px solid #1F1F1F",
        minHeight: 96, // tap target ≥ 96px (well above 48px floor)
      }}
    >
      <div className="h-full px-4 sm:px-6 py-5 flex items-center gap-4">
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-md shrink-0"
          style={{
            background: `${action.accent}1A`,
            color: action.accent,
            border: `1px solid ${action.accent}55`,
          }}
        >
          <Icon className="w-7 h-7" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg sm:text-xl font-black tracking-tight text-white leading-tight">
            {action.label}
          </div>
          <div className="text-sm mt-0.5" style={{ color: "#BDBDBD" }}>
            {action.sub}
          </div>
        </div>
        <ChevronRight className="w-6 h-6 shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: "#FF6A00" }} />
      </div>
    </Link>
  );
}
